/**
 * LanguagePairPicker
 *
 * Modal that appears when the "Machine Translation" template is selected.
 * The client (project creator) picks source → target language.
 * The resulting label_config XML is pre-filled with the actual language names
 * so annotators see a "hardcoded" instruction (e.g. "Translate from English to Hindi").
 *
 * The chosen ISO codes are also stored on the Project model for future annotator
 * filtering by translation capability.
 */

import React from "react";

// ISO 639-1 language list.  Add more as needed.
export const LANGUAGES = [
  { code: "af", name: "Afrikaans" },
  { code: "ar", name: "Arabic" },
  { code: "bn", name: "Bengali" },
  { code: "zh", name: "Chinese (Simplified)" },
  { code: "zh-tw", name: "Chinese (Traditional)" },
  { code: "cs", name: "Czech" },
  { code: "da", name: "Danish" },
  { code: "nl", name: "Dutch" },
  { code: "en", name: "English" },
  { code: "fi", name: "Finnish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "el", name: "Greek" },
  { code: "gu", name: "Gujarati" },
  { code: "he", name: "Hebrew" },
  { code: "hi", name: "Hindi" },
  { code: "hu", name: "Hungarian" },
  { code: "id", name: "Indonesian" },
  { code: "it", name: "Italian" },
  { code: "ja", name: "Japanese" },
  { code: "kn", name: "Kannada" },
  { code: "ko", name: "Korean" },
  { code: "ml", name: "Malayalam" },
  { code: "mr", name: "Marathi" },
  { code: "ms", name: "Malay" },
  { code: "ne", name: "Nepali" },
  { code: "no", name: "Norwegian" },
  { code: "pa", name: "Punjabi" },
  { code: "fa", name: "Persian" },
  { code: "pl", name: "Polish" },
  { code: "pt", name: "Portuguese" },
  { code: "ro", name: "Romanian" },
  { code: "ru", name: "Russian" },
  { code: "si", name: "Sinhala" },
  { code: "sk", name: "Slovak" },
  { code: "es", name: "Spanish" },
  { code: "sw", name: "Swahili" },
  { code: "sv", name: "Swedish" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "th", name: "Thai" },
  { code: "tr", name: "Turkish" },
  { code: "uk", name: "Ukrainian" },
  { code: "ur", name: "Urdu" },
  { code: "vi", name: "Vietnamese" },
];

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const card = {
  background: "#1a1a2e",
  border: "1px solid rgba(139,92,246,0.3)",
  borderRadius: 12,
  padding: "32px 36px",
  width: 480,
  maxWidth: "90vw",
  color: "#e2e8f0",
  fontFamily: "'Space Grotesk', system-ui, sans-serif",
};

const title = {
  fontSize: 18,
  fontWeight: 700,
  marginBottom: 8,
  color: "#f1f5f9",
};

const subtitle = {
  fontSize: 13,
  color: "#94a3b8",
  marginBottom: 24,
};

const row = {
  display: "flex",
  gap: 16,
  marginBottom: 24,
};

const fieldWrap = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const label = {
  fontSize: 12,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const select = {
  width: "100%",
  padding: "8px 12px",
  background: "#0f0f23",
  border: "1px solid rgba(139,92,246,0.4)",
  borderRadius: 6,
  color: "#e2e8f0",
  fontSize: 14,
  cursor: "pointer",
  outline: "none",
};

const actions = {
  display: "flex",
  gap: 12,
  justifyContent: "flex-end",
};

const btnPrimary = {
  padding: "8px 20px",
  background: "#8b5cf6",
  border: "none",
  borderRadius: 6,
  color: "#fff",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "'Space Grotesk', system-ui, sans-serif",
};

const btnCancel = {
  ...btnPrimary,
  background: "transparent",
  border: "1px solid rgba(139,92,246,0.4)",
  color: "#94a3b8",
};

const arrow = {
  display: "flex",
  alignItems: "center",
  paddingTop: 24,
  color: "#8b5cf6",
  fontSize: 22,
  userSelect: "none",
};

/**
 * @param {object} props
 * @param {function} props.onConfirm  — called with ({ sourceCode, sourceName, targetCode, targetName })
 * @param {function} props.onCancel
 */
export const LanguagePairPicker = ({ onConfirm, onCancel }) => {
  const [source, setSource] = React.useState("en");
  const [target, setTarget] = React.useState("es");

  const sourceName = LANGUAGES.find((l) => l.code === source)?.name ?? source;
  const targetName = LANGUAGES.find((l) => l.code === target)?.name ?? target;

  const handleConfirm = () => {
    if (source === target) {
      alert("Source and target languages must be different.");
      return;
    }
    onConfirm({ sourceCode: source, sourceName, targetCode: target, targetName });
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={card}>
        <div style={title}>Set Translation Language Pair</div>
        <div style={subtitle}>
          Annotators will be asked to translate from the source language into the target language. This is stored on the
          project so you can filter annotators by their translation capabilities.
        </div>

        <div style={row}>
          <div style={fieldWrap}>
            <span style={label}>Source language</span>
            <select
              style={select}
              value={source}
              onChange={(e) => setSource(e.target.value)}
              aria-label="Source language"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          <div style={arrow}>→</div>

          <div style={fieldWrap}>
            <span style={label}>Target language</span>
            <select
              style={select}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              aria-label="Target language"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={actions}>
          <button type="button" style={btnCancel} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" style={btnPrimary} onClick={handleConfirm}>
            Use {sourceName} → {targetName}
          </button>
        </div>
      </div>
    </div>
  );
};
